import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Confetti } from "@/components/effects/Confetti";
import { toast } from "@/components/shell/Toaster";
import { claimTreasureReward } from "@/lib/db/userPrefs";
import type { TreasurePrefs } from "@/lib/schemas";
import {
  drawRandomTreasureReward,
  type TreasureProgress,
  type TreasureReward,
} from "@/lib/utils/treasure";
import { TreasureRewardIcon } from "./TreasureRewardIcon";

type Stage = "closed" | "opening" | "won";

const TONE_CLASS: Record<TreasureReward["tone"], string> = {
  hay: "border-[#E8C967] bg-[#FFF8D9]",
  green: "border-[#AED0B1] bg-[#E9F6E8]",
  purple: "border-[#CFC7F8] bg-[#F1EEFF]",
  sky: "border-[#B9D9F5] bg-[#EAF5FF]",
};

const SLOT_LABEL: Record<TreasureReward["slot"], string> = {
  companion: "Nowy towarzysz",
  backpack: "Nowy plecak",
  glasses: "Nowe okulary",
  hoodie: "Nowa bluza",
  necklace: "Nowy łańcuch",
  hair: "Nowa fryzura",
  aura: "Nowa aura",
  scene: "Nowe tło pokoju",
};

export function TreasureModal({
  open,
  onClose,
  uid,
  progress,
  treasure,
  previewOnly = false,
  onPreviewWin,
}: {
  open: boolean;
  onClose: () => void;
  uid: string;
  progress: TreasureProgress;
  treasure: TreasurePrefs;
  previewOnly?: boolean;
  onPreviewWin?: (reward: TreasureReward) => void;
}) {
  const [stage, setStage] = useState<Stage>("closed");
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState<TreasureReward | null>(null);

  useEffect(() => {
    if (!open) return;
    setStage("closed");
    setBusy(false);
    setWon(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open) return null;

  const openChest = async () => {
    if (busy) return;
    const reward = drawRandomTreasureReward(treasure.ownedRewardIds);
    if (!reward) {
      toast.success("Masz już wszystkie gadżety z Polany!");
      onClose();
      return;
    }

    setBusy(true);
    setStage("opening");
    await new Promise((resolve) => window.setTimeout(resolve, 850));

    try {
      if (!previewOnly) {
        await claimTreasureReward(uid, progress.earnedChestCount, reward.id);
      }
      onPreviewWin?.(reward);
      setWon(reward);
      setStage("won");
    } catch (error) {
      setStage("closed");
      toast.error(
        error instanceof Error
          ? error.message
          : "Nie udało się otworzyć skrzynki",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Skrzynka z losowym gadżetem"
      className="treasure-meadow-bg fixed inset-0 z-[60] flex items-center justify-center overflow-hidden px-4 py-6"
    >
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(24,60,35,0.05)_0%,rgba(20,45,28,0.62)_100%)]" />
      {stage === "won" && <Confetti />}

      <button
        type="button"
        onClick={onClose}
        disabled={busy}
        className="absolute right-4 top-[max(16px,env(safe-area-inset-top))] z-20 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-navy shadow-lg disabled:opacity-50"
        aria-label="Zamknij"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center text-center">
        {(stage === "closed" || stage === "opening") && (
          <>
            <div className="rounded-full bg-white/85 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-forest shadow-sm">
              Gwarantowany nowy gadżet
            </div>
            <h2 className="mt-3 font-display text-[30px] font-extrabold leading-tight text-white drop-shadow-md">
              {stage === "opening" ? "Co jest w środku?" : "Skrzynka z Polany"}
            </h2>
            <p className="mt-1 max-w-[31ch] text-[13px] font-medium leading-snug text-white/90 drop-shadow-sm">
              Losujemy jeden przedmiot spośród tych, których jeszcze nie masz.
              Bez duplikatów i bez pustych skrzynek.
            </p>
            <button
              type="button"
              onClick={() => void openChest()}
              disabled={busy}
              className={`mt-4 rounded-[32px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sunny/80 ${
                stage === "opening"
                  ? "treasure-chest-opening"
                  : "treasure-chest-ready"
              }`}
              aria-label="Otwórz skrzynkę i wylosuj gadżet"
            >
              <img
                src="/treasure/chest.webp?v=1"
                alt="Zamknięta drewniana skrzynka z zielonym liściem"
                className="w-[270px] max-w-[76vw] drop-shadow-[0_22px_25px_rgba(24,44,22,0.45)]"
              />
            </button>
            <Button
              size="lg"
              variant="sunny"
              className="mt-3 min-w-56"
              disabled={busy}
              onClick={() => void openChest()}
            >
              {stage === "opening" ? "Losuję…" : "Losuj mój gadżet"}
            </Button>
          </>
        )}

        {stage === "won" && won && (
          <div className="w-full rounded-[28px] border border-white/70 bg-white/95 p-6 shadow-2xl treasure-reward-reveal">
            <div
              className={`relative mx-auto grid h-32 w-32 place-items-center overflow-hidden rounded-[32px] border-2 shadow-inner ${TONE_CLASS[won.tone]}`}
            >
              <span className="absolute right-2 top-2 text-sunny">
                <Sparkles className="h-5 w-5" />
              </span>
              <TreasureRewardIcon reward={won} className="h-24 w-24" />
            </div>
            <div className="mt-4 text-[11px] font-extrabold uppercase tracking-[0.12em] text-forest/70">
              {SLOT_LABEL[won.slot]}
            </div>
            <h2 className="mt-1 font-display text-[27px] font-extrabold text-navy">
              {won.title}
            </h2>
            <p className="mt-1 text-[13px] text-slate">
              {won.description} Gadżet został od razu założony na Twojego
              bohatera.
            </p>
            <Button block size="lg" className="mt-5" onClick={onClose}>
              Pokaż na bohaterze
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
